/**
 * Dex live-scan frame gate — Push 1 / v1.4.3 b1.
 *
 * First-run accuracy fix. Before Push 1 the live scanner applied any
 * frame whose adapter result reported `mode: "grid_4x4"` with at least
 * one parsed dex number above a permissive (35) confidence floor. On the
 * very first live run that let three classes of bad frame mutate Dex
 * state:
 *
 *   1. The iOS ReplayKit broadcast picker / "Start Broadcast" overlay and
 *      the red "Screen Broadcast" status banner. Their text ("Screen
 *      Broadcast", "on vou", clock digits) occasionally OCRs a stray
 *      1-3 digit number that `parseDexNumber` happily accepts.
 *   2. The Nexus Dex app's own UI (the user has not swapped to Pokémon GO
 *      yet on the first frames).
 *   3. Region-overview / non-grid Pokémon GO screens.
 *
 * This module is a PURE, side-effect-free pre-apply gate. It inspects the
 * `DexLiveFrameResult` (which now carries a `gridSignature`) and decides
 * whether the frame is allowed to reach the session's apply path. It does
 * NOT mutate Dex state and never throws.
 *
 * The contract is deliberately conservative: when in doubt, reject. A
 * rejected frame is still surfaced to diagnostics (with a categorised
 * reason) so the panel can show the user WHY nothing applied, rather than
 * silently dropping it.
 */

import type { DexLiveFrameResult } from "./dexLiveFrameAdapter";

/**
 * Why a frame was (not) allowed to mutate Dex state. Mirrors the reason
 * vocabulary the user asked for in the diagnostics panel.
 */
export type DexFrameGateReason =
  | "ok"
  | "not_pokedex_grid"
  | "overlay_frame"
  | "low_confidence"
  | "no_numbers";

export interface DexFrameGateResult {
  /** True only when the frame is a trustworthy Pokémon GO Pokédex grid. */
  accept: boolean;
  reason: DexFrameGateReason;
  /** Strength of the grid signature 0-1, for diagnostics + stability tuning. */
  signatureStrength: number;
}

/**
 * Confidence floor a frame must clear before its numbers may apply. This
 * is higher than the legacy adapter floor (35) on purpose — first-run
 * accuracy beats coverage, and a frame below this is far more likely to
 * be overlay/garbage text than a clean grid read.
 */
export const GATE_MIN_CONFIDENCE = 55;

/**
 * Tokens that betray an iOS broadcast picker / status overlay / our own
 * app chrome rather than the Pokémon GO Pokédex. Matched case-insensitively
 * against any OCR text the adapter forwarded in the grid signature.
 */
const OVERLAY_TOKENS: RegExp[] = [
  /screen\s*broadcast/i,
  /start\s*broadcast/i,
  /stop\s*broadcast/i,
  /\bbroadcast\b/i,
  /microphone/i,
  /\bnexus\b/i,
  /\bgodex\b/i,
  /on\s*vou/i, // observed broadcast-banner OCR garble
];

/**
 * Compute a 0-1 strength score for the "this is a real Pokédex grid"
 * signature. Used both to gate and to relax the stability requirement
 * when the signal is unambiguous. Pure.
 */
export function gridSignatureStrength(result: DexLiveFrameResult): number {
  const sig = result.gridSignature;
  if (!sig) return 0;
  let score = 0;
  // Multiple dex numbers across multiple rows is the single strongest
  // signal of a 4×4 grid (overlays/banners yield at most a stray digit).
  if (sig.numbersRead >= 2) score += 0.35;
  if (sig.numbersRead >= 6) score += 0.15;
  if (sig.distinctRows >= 2) score += 0.2;
  // A readable region tab or filter chip means we are inside the Pokédex.
  if (sig.regionKnown) score += 0.15;
  if (sig.filterKnown) score += 0.1;
  // A plausible in-range progress total ("138 / 151") is a clean grid tell.
  if (sig.progressInRange) score += 0.05;
  return Math.min(1, score);
}

/**
 * Decide whether a live frame may mutate Dex state. Conservative: the
 * default is rejection. Order of checks matters — overlay detection comes
 * first so a banner that happens to clear the confidence floor is still
 * caught.
 */
/**
 * The Pokédex list's own chrome: the region name in the header card, the
 * filter pills, or an in-range progress total. A real grid ALWAYS shows at
 * least one of them; the map, a rewards sheet or a power-spot picker never
 * do, however many digits they happen to print.
 */
export function hasPokedexChrome(result: DexLiveFrameResult): boolean {
  const sig = result.gridSignature;
  // A region from the tab-bar column pixel guess is not chrome (2026-09-23):
  // `regionRead` is the text-read subset of `regionKnown`.
  const regionRead = sig ? (sig.regionRead ?? sig.regionKnown) : false;
  if (sig && (regionRead || sig.filterKnown || sig.progressInRange)) return true;
  // The variant pill row ("LUCKY", "XXL", "★ 100%") read as text (2026-09-22
  // Galaxy: the 100% and XXL pages did not scan at all). This GO layout has
  // no Seen / Caught filter row and its header shows a bare count, so on
  // those pages the region label was the ONLY chrome, and pink-on-pink or
  // blue-on-blue header text is where OCR is weakest. The pill row is read
  // at 0.97 confidence on every page and exists nowhere but the Pokédex.
  return result.variantSource === "tab-only" || result.variantSource === "tab+tint";
}

export interface DexFrameGateOptions {
  /**
   * Pokédex chrome was read on a frame within the last few seconds (the
   * caller's lock, see dexLiveScanSession). A header blurred by a fast
   * scroll then does not drop a real scan for one frame.
   */
  chromeRecentlySeen?: boolean;
}

export function gateDexLiveFrame(
  result: DexLiveFrameResult,
  opts: DexFrameGateOptions = {},
): DexFrameGateResult {
  const sig = result.gridSignature;
  const strength = gridSignatureStrength(result);

  // 1. Overlay / non-Pokédex-app frames. The OCR pipeline flags these as
  //    `not_pokedex_screenshot`; we ALSO scan any forwarded text for the
  //    broadcast/app chrome tokens so a stray-digit banner can't sneak by.
  const overlayText = sig?.text ?? "";
  const hasOverlayToken = OVERLAY_TOKENS.some((re) => re.test(overlayText));
  const flaggedNotPokedex = sig?.warnings?.includes("not_pokedex_screenshot") ?? false;
  if (hasOverlayToken) {
    return { accept: false, reason: "overlay_frame", signatureStrength: strength };
  }

  // 2. No usable grid: nothing parsed, or the OCR itself said this is not
  //    a Pokédex screenshot and the signature is weak.
  if (result.numbersRead === 0) {
    return { accept: false, reason: "no_numbers", signatureStrength: strength };
  }
  if (flaggedNotPokedex && strength < 0.5) {
    return { accept: false, reason: "not_pokedex_grid", signatureStrength: strength };
  }
  // 2b. No Pokédex chrome at all (2026-09-22 Galaxy photos): raid timers on
  //     the map, "8,500 XP" and item counts on a rewards sheet, three CPs on
  //     a power-spot picker all read as "several numbers across several
  //     rows" and cleared the strength floor below, so the scan coached the
  //     trainer to scroll a screen that was not the Pokédex and WROTE the
  //     digits it read as dex entries. The header's region name, the filter
  //     pills or an in-range progress total must be on the frame, or have
  //     been within the caller's lock window.
  if (!hasPokedexChrome(result) && !opts.chromeRecentlySeen) {
    return { accept: false, reason: "not_pokedex_grid", signatureStrength: strength };
  }

  // 3. Weak grid signature → treat as not-a-grid even if a number parsed.
  //    A single stray digit with no region/filter/row diversity is exactly
  //    what overlay text produces.
  if (strength < 0.35) {
    return { accept: false, reason: "not_pokedex_grid", signatureStrength: strength };
  }

  // 4. Confidence floor. Below this we don't trust the read enough to
  //    mutate state on the strength of one frame.
  if (result.confidence < GATE_MIN_CONFIDENCE) {
    return { accept: false, reason: "low_confidence", signatureStrength: strength };
  }

  return { accept: true, reason: "ok", signatureStrength: strength };
}
